import { Search } from 'lucide-react'
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover'
import { cn } from '@/utils'

type TraySearchSuggestInputProps = {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Matches for what's typed, best first. The dropdown only shows while this is non-empty. */
  suggestions: string[]
  /** Reports the dropdown opening/closing — feed it to the tray's `useAnyDropdownOpen` so the
   *  panel's Escape chain closes this list before the panel. */
  onOpenChange?: (open: boolean) => void
  /** Accessible name — defaults to `placeholder`, which is otherwise the only visible label. */
  ariaLabel?: string
  className?: string
}

/** `value` with every RegExp metacharacter escaped, for a literal match. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Renders `text` with its first case-insensitive match of `query` emphasized. Matched on `text`
 * itself (an `i` RegExp), not on an upper-cased copy — upper-casing can change a string's length
 * ("ß" → "SS"), which would shift the highlight.
 */
function HighlightedMatch({ text, query }: { text: string; query: string }) {
  const needle = query.trim()
  const match = needle ? new RegExp(escapeRegExp(needle), 'i').exec(text) : null

  if (!match) return <>{text}</>

  const start = match.index
  const end = start + match[0].length

  return (
    <>
      {text.slice(0, start)}
      <span className="font-semibold text-foreground">{text.slice(start, end)}</span>
      {text.slice(end)}
    </>
  )
}

/**
 * `TraySearchInput` with a suggestions dropdown under it (an ARIA combobox). Typing opens the
 * list; ArrowUp/ArrowDown move through it, Enter or a click picks a suggestion (it replaces the
 * field's text), and Escape, blur or an outside click closes it. Focus never leaves the input.
 */
export function TraySearchSuggestInput({
  value,
  onChange,
  placeholder,
  suggestions,
  onOpenChange,
  ariaLabel,
  className,
}: TraySearchSuggestInputProps) {
  const listboxId = useId()
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [wantsOpen, setWantsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)

  const open = wantsOpen && suggestions.length > 0
  const activeIndex = highlightIndex < suggestions.length ? highlightIndex : -1

  const onOpenChangeRef = useRef(onOpenChange)

  onOpenChangeRef.current = onOpenChange

  useEffect(() => {
    onOpenChangeRef.current?.(open)
  }, [open])

  const close = () => {
    setWantsOpen(false)
    setHighlightIndex(-1)
  }

  const pick = (suggestion: string) => {
    onChange(suggestion)
    close()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (suggestions.length === 0) return
      event.preventDefault()

      if (!open) {
        setWantsOpen(true)
        setHighlightIndex(event.key === 'ArrowDown' ? 0 : suggestions.length - 1)
        return
      }

      const step = event.key === 'ArrowDown' ? 1 : -1

      setHighlightIndex((activeIndex + step + suggestions.length) % suggestions.length)
    } else if (event.key === 'Enter' && open && activeIndex >= 0) {
      event.preventDefault()
      pick(suggestions[activeIndex])
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className={cn(
            'flex h-9 w-full min-w-0 items-center gap-1 overflow-hidden rounded-md border border-input bg-background px-3 py-1 shadow-xs',
            className
          )}
        >
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            autoComplete="off"
            placeholder={placeholder}
            aria-label={ariaLabel ?? placeholder}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-activedescendant={
              open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
            }
            value={value}
            onChange={(event) => {
              onChange(event.target.value)
              setWantsOpen(true)
              setHighlightIndex(-1)
            }}
            onKeyDown={handleKeyDown}
            onBlur={close}
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-1"
        // Keep focus (and typing) in the input; the list is driven from its keydown.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault()
        }}
      >
        <ul id={listboxId} role="listbox" aria-label={`${ariaLabel ?? placeholder} suggestions`}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              // The match highlight splits the text into spans; name the option explicitly so
              // assistive tech reads the whole filename, not its pieces.
              aria-label={suggestion}
              // mousedown, not click: picking must not blur the input (which closes the list).
              onMouseDown={(event) => {
                event.preventDefault()
                pick(suggestion)
              }}
              onMouseEnter={() => setHighlightIndex(index)}
              className={cn(
                'cursor-pointer truncate rounded-sm px-2 py-1.5 text-sm text-muted-foreground',
                index === activeIndex && 'bg-accent text-accent-foreground'
              )}
              title={suggestion}
            >
              <HighlightedMatch text={suggestion} query={value} />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
